import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Row,
  Col,
  Typography,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tag,
} from "antd";
import { PlusOutlined, UserOutlined, MailOutlined, LockOutlined } from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import moment from "moment";

const { Title } = Typography;

const BusCompanyList = () => {
  const { request } = useRequest();
  const [companies, setCompanies] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
  const [form] = Form.useForm();

  const fetchCompanies = (page = 1, limit = 10) => {
    setLoading(true);
    request({
      url: "/admin/bus-users",
      method: "GET",
      params: { page, limit },
      onSuccess: (data) => {
        const busCompanies = data.users.filter(u => u.userType === 'bus_company');
        setCompanies(busCompanies);
        setPagination({
          total: data.total || 0,
          page: data.page || page,
          limit: limit,
          totalPages: data.totalPages || 0,
        });
        setLoading(false);
      },
      onError: () => setLoading(false),
    });
  };

  const fetchHotels = () => {
    request({
      url: '/admin/active-hotels',
      method: "GET",
      onSuccess: (data) => {
        setHotels(data.hotels || []);
      },
    });
  };

  useEffect(() => {
    fetchCompanies(1, pagination.limit);
    fetchHotels();
  }, []);

  const handleCreate = (values) => {
    setLoading(true);
    request({
      url: apiPath.createBusCompany,
      method: "POST",
      data: values,
      onSuccess: (data) => {
        ShowToast(data.message, Severty.SUCCESS);
        setModalVisible(false);
        form.resetFields();
        fetchCompanies(1, pagination.limit);
      },
      onError: (err) => {
        setLoading(false);
        ShowToast(err.response?.data?.message || "Failed to create bus company", Severty.ERROR);
      },
    });
  };

  const handleStatusChange = (record) => {
    setLoading(true);
    request({
      url: apiPath.updateBusStatus(record._id),
      method: "POST",
      onSuccess: (data) => {
        ShowToast(data.message, Severty.SUCCESS);
        fetchCompanies(pagination.page, pagination.limit);
      },
      onError: (err) => {
        setLoading(false);
        ShowToast(err.response?.data?.message || "Failed to update status", Severty.ERROR);
      },
    });
  };

  const columns = [
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Linked Hotel Id",
      dataIndex: "Hotel_Id",
      key: "Hotel_Id",
      // render: (hotelId) => {
      //   const hotel = hotels.find(h => h._id === hotelId);
      //   return hotel ? hotel.Hotel_name : "N/A";
      // }
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Tag
          color={status === 1 ? "green" : "red"}
          style={{ cursor: "pointer" }}
          onClick={() => handleStatusChange(record)}
        >
          {status === 1 ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
  ];

  return (
    // <Main>
    <div className="layout-content">
      <Card
        bordered={false}
        className="criclebox h-full"
        title={
          <Title level={4} style={{ margin: 0 }}>
            Bus Companies
          </Title>

        }
        extra={

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            Create Bus Company
          </Button>

        }
      >
        <Table
          className="table-responsive"
          columns={columns}
          dataSource={companies}
          loading={loading}
          rowKey="_id"
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total, range) =>
              `Showing ${range[0]}–${range[1]} of ${total} bus compan${total !== 1 ? "ies" : "y"}`,
            onChange: (page, pageSize) => {
              setPagination((prev) => ({ ...prev, page, limit: pageSize }));
              fetchCompanies(page, pageSize);
            },
          }}
        />
      </Card>

      <Modal
        title="Create New Bus Company"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="companyName"
            label="Company Name"
            rules={[{ required: true, whitespace: true, message: "Please enter company name" }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Enter company name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter a valid email" }
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Enter email" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
                {
                  required: true,
                  message: "Please enter your password",
                },
                {
                  min: 8,
                  message: "Password must be at least 8 characters long",
                },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^#()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
                  message:
                    "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character",
                }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Enter password" />
          </Form.Item>
          <Form.Item
            name="hotelId"
            label="Linked Hotel"
            rules={[{ required: true, message: "Please select a hotel" }]}
          >
            <Select placeholder="Select a hotel">
              {hotels.map(hotel => (
                <Select.Option key={hotel._id} value={hotel._id}>
                  {hotel.Hotel_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
    // </Main>
  );
};

export default BusCompanyList;
